import { useCallback, useEffect, useMemo, useState } from "react";
import { ClipboardCheck, RefreshCw, Search } from "lucide-react";
import { Button, EmptyState, ErrorBanner, LoadingState, PageHeader, StatusBadge } from "../components/Ui";
import { useAuth } from "../context/AuthContext";
import { pickups, warehouseName } from "../lib/demoData";
import { formatDateTime, loadWarehouseSnapshot, type PickupSnapshot } from "../lib/warehouseData";
import { OrderLink } from "../components/OrderLink";
import { useDeveloperSettings } from "../lib/developerSettings";

export function PickupsPage() {
  const { cloudConfigured } = useAuth();
  const { exampleTexts } = useDeveloperSettings();
  const [rows, setRows] = useState<PickupSnapshot[]>([]);
  const [loading, setLoading] = useState(cloudConfigured);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    if (!cloudConfigured) return;
    setLoading(true);
    try {
      setRows((await loadWarehouseSnapshot()).pickups);
      setError("");
    } catch (loadError) {
      setError(`Abholungen konnten nicht geladen werden: ${loadError instanceof Error ? loadError.message : "Unbekannter Fehler"}`);
    } finally {
      setLoading(false);
    }
  }, [cloudConfigured]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const term = search.trim().toLocaleLowerCase("de-DE");
    if (!term) return rows;
    return rows.filter((pickup) => [pickup.technician, pickup.warehouseName, ...pickup.orderNumbers, ...pickup.packageCodes]
      .some((value) => value.toLocaleLowerCase("de-DE").includes(term)));
  }, [rows, search]);

  if (!cloudConfigured) return <>
    <PageHeader title="Abholungen" description="Lokaler Vorschaumodus ohne Cloudverbindung." />
    <div className="table-shell"><table><thead><tr><th>Auftrag</th><th>Monteur</th><th>Lager</th><th>Packstücke</th><th>Zeitpunkt</th><th>Nachweis</th></tr></thead><tbody>{pickups.map((pickup) => <tr key={pickup.id}><td><strong><OrderLink orderNumber={pickup.orderNumber} /></strong></td><td>{pickup.technician}</td><td>{warehouseName(pickup.warehouse)}</td><td>{pickup.packageCount}</td><td>{pickup.pickedUpAt}</td><td><StatusBadge tone="success"><ClipboardCheck size={13} /> Demo</StatusBadge></td></tr>)}</tbody></table></div>
  </>;

  return (
    <>
      <PageHeader title="Abholungen" description="Nachweis über vollständig ausgegebene Packstücke und Unterschriften." action={<Button variant="secondary" onClick={load} disabled={loading}><RefreshCw size={16} /> Aktualisieren</Button>} />
      {error && <ErrorBanner message={error} />}
      <div className="toolbar"><label className="search-box"><Search size={18} /><input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={exampleTexts.pickupsSearch} /></label></div>
      {loading ? <LoadingState label="Abholungen werden geladen …" /> : filtered.length === 0 ? (
        <EmptyState title="Noch keine Abholungen" description="Unterschriebene Ausgaben aus den Lagergeräten erscheinen hier." />
      ) : (
        <div className="table-shell">
          <table>
            <thead><tr><th>Auftrag</th><th>Monteur</th><th>Lager</th><th>Packstücke</th><th>Zeitpunkt</th><th>Nachweis</th></tr></thead>
            <tbody>{filtered.map((pickup) => <tr key={pickup.id}>
              <td><strong className="order-link-list">{pickup.orderNumbers.length ? pickup.orderNumbers.map((number, index) => <span key={number}>{index > 0 && ", "}<OrderLink orderNumber={number} /></span>) : "–"}</strong><small className="table-subline">{pickup.packageCodes.join(", ")}</small></td>
              <td>{pickup.technician}</td>
              <td>{pickup.warehouseName}</td>
              <td>{pickup.packageCount}</td>
              <td>{formatDateTime(pickup.signedAt ?? pickup.createdAt)}</td>
              <td><StatusBadge tone={pickup.signedAt ? "success" : "warning"}><ClipboardCheck size={13} /> {pickup.signedAt ? "Unterschrieben" : "Entwurf"}</StatusBadge></td>
            </tr>)}</tbody>
          </table>
        </div>
      )}
    </>
  );
}
